import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, RotateCcw, BookOpen, Share2, X, ChevronRight, Heart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MiracleDeck = ({ isOpen, onClose }) => {
  const [view, setView] = useState("pull"); // pull, reveal, card
  const [currentCard, setCurrentCard] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [alreadyPulled, setAlreadyPulled] = useState(false);
  const [reflection, setReflection] = useState("");
  const [showReflection, setShowReflection] = useState(false);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  useEffect(() => {
    if (isOpen) {
      checkDailyCard();
    }
  }, [isOpen]);

  const checkDailyCard = async () => {
    try {
      const res = await axios.get(`${API}/miracle-deck/daily-pull`, getAuthHeader());
      if (res.data.already_pulled) {
        setCurrentCard(res.data.card);
        setAlreadyPulled(true);
        setIsRevealed(true);
        setView("card");
      } else {
        setCurrentCard(res.data.card);
        setAlreadyPulled(false);
        setView("pull");
      }
    } catch (err) {
      console.error("Error checking daily card:", err);
    }
  };

  const pullCard = async () => {
    setIsFlipping(true);
    
    // Animate card flip
    setTimeout(() => {
      setIsRevealed(true);
      setView("card");
      setIsFlipping(false);
      toast.success("Your guidance has been revealed! ✨");
    }, 1500);
  };

  const pullAnotherCard = async () => {
    try {
      setIsFlipping(true);
      setIsRevealed(false);
      const res = await axios.get(`${API}/miracle-deck/pull`, getAuthHeader());
      setCurrentCard(res.data.card);
      
      setTimeout(() => {
        setIsRevealed(true);
        setIsFlipping(false);
      }, 1500);
    } catch (err) {
      toast.error("Failed to pull card");
      setIsFlipping(false);
    }
  };

  const saveReflection = async () => {
    if (!reflection.trim()) return;
    try {
      await axios.post(
        `${API}/miracle-deck/journal?card_id=${currentCard.id}&reflection=${encodeURIComponent(reflection)}`,
        {},
        getAuthHeader()
      );
      toast.success("Reflection saved! 📝");
      setShowReflection(false);
      setReflection("");
    } catch (err) {
      toast.error("Failed to save reflection");
    }
  };

  const shareCard = () => {
    if (navigator.share && currentCard) {
      navigator.share({
        title: `${currentCard.name} - Rooted Manifestation Miracle Deck`,
        text: `"${currentCard.message}"\n\n${currentCard.guidance}\n\nPulled from my Lala Land Rooted Manifestation Deck ✨`,
      });
    } else {
      navigator.clipboard.writeText(`"${currentCard.message}" - ${currentCard.name}\n\nFrom my Lala Land Rooted Manifestation Deck ✨`);
      toast.success("Card copied to clipboard!");
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900 max-h-[90vh] overflow-y-auto">
        {/* Starry Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                opacity: Math.random() * 0.7 + 0.3
              }}
            />
          ))}
        </div>

        <div className="relative p-5 pt-10 pb-20 min-h-[600px] flex flex-col">
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white flex flex-col items-center justify-center gap-1">
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                Rooted Manifestation
              </span>
              <span>Miracle Deck</span>
            </h2>
            <p className="text-white/60 text-sm mt-2">Receive your divine guidance</p>
          </div>

          {/* Card Area */}
          <div className="flex-1 flex items-center justify-center">
            {view === "pull" && !isFlipping && (
              <div className="text-center">
                {/* Card Back */}
                <div 
                  onClick={pullCard}
                  className="w-56 h-80 mx-auto rounded-2xl bg-gradient-to-br from-purple-600 via-violet-700 to-indigo-800 shadow-2xl shadow-purple-500/30 cursor-pointer transform hover:scale-105 transition-all duration-300 flex items-center justify-center border border-purple-400/30 relative overflow-hidden"
                >
                  {/* Decorative pattern */}
                  <div className="absolute inset-4 border-2 border-purple-400/30 rounded-xl" />
                  <div className="absolute inset-8 border border-purple-400/20 rounded-lg" />
                  
                  <div className="text-center">
                    <span className="text-5xl block mb-3">✨</span>
                    <p className="text-white/80 font-medium">Tap to Reveal</p>
                    <p className="text-white/50 text-xs mt-1">Your Guidance</p>
                  </div>
                </div>
                
                <p className="text-white/50 text-sm mt-4">
                  {alreadyPulled ? "You've already pulled today's card" : "Focus on your question, then tap"}
                </p>
              </div>
            )}

            {/* Flipping Animation */}
            {isFlipping && (
              <div className="w-56 h-80 mx-auto perspective-1000">
                <div className="w-full h-full animate-flip-card">
                  <div className="w-full h-full rounded-2xl bg-gradient-to-br from-purple-600 via-violet-700 to-indigo-800 shadow-2xl shadow-purple-500/30 flex items-center justify-center">
                    <span className="text-5xl animate-pulse">✨</span>
                  </div>
                </div>
              </div>
            )}

            {/* Revealed Card */}
            {view === "card" && isRevealed && currentCard && (
              <div className="w-full max-w-sm">
                <div 
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  style={{ 
                    background: `linear-gradient(135deg, ${currentCard.color}20, ${currentCard.color}40)`,
                    borderColor: currentCard.color,
                    borderWidth: 2
                  }}
                >
                  {/* Card Header */}
                  <div 
                    className="p-4 text-center"
                    style={{ backgroundColor: `${currentCard.color}30` }}
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="text-lg">{currentCard.category_info?.emoji}</span>
                      <span className="text-xs text-white/70 uppercase tracking-wider">
                        {currentCard.category_info?.name}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{currentCard.name}</h3>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 bg-slate-900/80 space-y-4">
                    {/* Message */}
                    <div className="text-center">
                      <p className="text-lg font-medium text-white italic">
                        "{currentCard.message}"
                      </p>
                    </div>

                    {/* Guidance */}
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-sm text-white/80 leading-relaxed">
                        {currentCard.guidance}
                      </p>
                    </div>

                    {/* Affirmation */}
                    <div 
                      className="p-3 rounded-lg text-center"
                      style={{ backgroundColor: `${currentCard.color}20` }}
                    >
                      <p className="text-xs text-white/60 mb-1">Affirmation</p>
                      <p className="text-sm font-medium text-white">
                        "{currentCard.affirmation}"
                      </p>
                    </div>

                    {/* Action */}
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-start gap-2">
                        <span className="text-amber-400">⚡</span>
                        <div>
                          <p className="text-xs text-amber-400 mb-1">Today's Action</p>
                          <p className="text-sm text-white/90">{currentCard.action}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={pullAnotherCard}
                    className="flex-1 border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Pull Again
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowReflection(true)}
                    className="flex-1 border-pink-500/30 text-pink-300 hover:bg-pink-500/20"
                  >
                    <BookOpen className="w-4 h-4 mr-1" />
                    Journal
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={shareCard}
                    className="border-amber-500/30 text-amber-300 hover:bg-amber-500/20"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Reflection Modal */}
        {showReflection && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-5 z-10">
            <div className="bg-slate-900 rounded-xl p-5 w-full max-w-sm border border-purple-500/30">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-400" />
                Journal Your Reflection
              </h3>
              <p className="text-sm text-white/60 mb-3">
                What does "{currentCard?.name}" mean to you today?
              </p>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                placeholder="Write your thoughts..."
                className="w-full p-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-white/40 resize-none h-32 mb-4"
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowReflection(false)}
                  className="flex-1 border-slate-600 text-white"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={saveReflection}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Save Reflection
                </Button>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes flip-card {
            0% { transform: rotateY(0deg); }
            50% { transform: rotateY(90deg); }
            100% { transform: rotateY(180deg); }
          }
          .animate-flip-card {
            animation: flip-card 1.5s ease-in-out;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default MiracleDeck;
